import {useMemo, useState} from "react";
import {v4 as UUID} from "uuid";
import {imageToGrid} from "@/dungeons/geometry/Coordinates.ts";
import type {DungeonLight, ImagePoint, LevelGeometryDocument} from "@/dungeons/model/DungeonGeometry.ts";
import {LIGHT_PRESETS, type LightPreset} from "@/dungeons/lighting/LightPresets.ts";
import type {GeometryDocumentUpdate} from "@/dungeons/editor/shared/useLevelGeometryDocument.ts";

export default function useDungeonLights({document, updateDocument}: {
    document: LevelGeometryDocument;
    updateDocument: (update: GeometryDocumentUpdate) => void;
}) {
    const [selectedId, setSelectedId] = useState<string>();
    const [newPreset, setNewPreset] = useState<Exclude<LightPreset, "CUSTOM">>("TORCH");
    const [preview, setPreview] = useState<{id: string; base?: DungeonLight; changes: Partial<DungeonLight>}>();
    const displayLights = useMemo(() => document.lights.map(light => light.id === preview?.id && light === preview.base
        ? {...light, ...preview.changes} : light), [document.lights, preview]);
    const selected = displayLights.find(light => light.id === selectedId);

    const previewChange = (id: string, changes: Partial<DungeonLight>) =>
        setPreview({id, base: document.lights.find(light => light.id === id), changes});
    const commitPreview = (id: string, changes: Partial<DungeonLight>) => {
        setPreview(undefined);
        const original = document.lights.find(light => light.id === id);
        if (original && Object.entries(changes).some(([key, value]) => original[key as keyof DungeonLight] !== value)) {
            update(id, changes);
        }
    };
    const select = (id: string | undefined) => {
        setPreview(undefined);
        setSelectedId(id);
    };

    const place = (imagePoint: ImagePoint) => {
        const light: DungeonLight = {
            id: UUID(), position: imageToGrid(imagePoint, document.registration),
            preset: newPreset, ...LIGHT_PRESETS[newPreset], enabled: true,
        };
        updateDocument(current => ({...current, lights: [...current.lights, light]}));
        setSelectedId(light.id);
    };
    const update = (id: string, changes: Partial<Omit<DungeonLight, "id">>) => {
        setPreview(undefined);
        updateDocument(current => ({...current, lights: current.lights.map(light =>
            light.id === id ? {...light, ...changes} : light)}));
    };
    const move = (id: string, imagePoint: ImagePoint) => {
        setPreview(undefined);
        updateDocument(current => ({...current, lights: current.lights.map(light =>
            light.id === id ? {...light, position: imageToGrid(imagePoint, current.registration)} : light)}));
    };
    const remove = (id: string) => {
        updateDocument(current => ({...current, lights: current.lights.filter(light => light.id !== id)}));
        setSelectedId(undefined);
    };
    return {selected, selectedId, displayLights, select, previewChange, commitPreview,
        newPreset, setNewPreset, place, update, move, remove};
}

export type DungeonLightsController = ReturnType<typeof useDungeonLights>;
